import { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Calendar, Users, Mail, Phone, Plus, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOOKING_STATUS_OPTIONS = [
  { value: 'planificando', label: 'Planificando', color: 'bg-blue-100 text-blue-800' },
  { value: 'confirmado_pendiente_pago', label: 'Confirmado (Pendiente Pago)', color: 'bg-orange-100 text-orange-800' },
  { value: 'confirmado_pagado', label: 'Confirmado y Pagado', color: 'bg-green-100 text-green-800' },
  { value: 'cerrado', label: 'Cerrado', color: 'bg-gray-100 text-gray-800' },
  { value: 'cancelado', label: 'Cancelado', color: 'bg-red-100 text-red-800' }
];

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [trips, setTrips] = useState([]);
  const [newBooking, setNewBooking] = useState({
    trip_id: '',
    trip_title: '',
    name: '',
    email: '',
    phone: '',
    num_travelers: 1,
    travel_date: '',
    end_date: '',
    special_requests: '',
    budget: '',
    price: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchBookings = async () => {
    try {
      const res = await axios.get(`${API}/bookings`, { headers });
      setBookings(res.data);
    } catch (err) {
      console.error('Error fetching bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const res = await axios.get(`${API}/trips`);
        setTrips(res.data);
      } catch (err) {
        console.error('Error fetching trips:', err);
      }
    };
    fetchTrips();
  }, []);

  const updateStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status?status=${status}`, {}, { headers });
      toast.success('Estado actualizado');
      fetchBookings();
    } catch (err) {
      toast.error('Error al actualizar');
    }
  };

  const handleCreateBooking = async () => {
    if (!newBooking.trip_title || !newBooking.name || !newBooking.email || !newBooking.phone || !newBooking.travel_date) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    try {
      await axios.post(`${API}/bookings`, newBooking, { headers });
      toast.success('Reserva creada correctamente');
      setOpenDialog(false);
      setNewBooking({
        trip_id: '',
        trip_title: '',
        name: '',
        email: '',
        phone: '',
        num_travelers: 1,
        travel_date: '',
        end_date: '',
        special_requests: '',
        budget: '',
        price: ''
      });
      fetchBookings();
    } catch (err) {
      console.error('Error creating booking:', err);
      toast.error('Error al crear la reserva');
    }
  };

  const getStatusBadge = (status) => {
    const statusOption = BOOKING_STATUS_OPTIONS.find(opt => opt.value === status);
    if (!statusOption) return null;
    return (
      <Badge className={statusOption.color}>
        {statusOption.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      return format(new Date(dateStr), "d MMM yyyy, HH:mm", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const filteredBookings = statusFilter === 'all' 
    ? bookings 
    : bookings.filter(b => b.status === statusFilter);

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-serif font-semibold text-primary" data-testid="admin-bookings-title">
              Reservas
            </h1>
            <p className="text-muted-foreground">
              {bookings.length} reservas en total
            </p>
          </div>
          
          <Button 
            onClick={() => setOpenDialog(true)}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Reserva
          </Button>
        </div>

        {/* Filtro de estado */}
        <div className="flex items-center gap-3 mb-4">
          <Filter className="w-5 h-5 text-muted-foreground" />
          <label className="text-sm font-medium">Filtrar por estado:</label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {BOOKING_STATUS_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton h-24 rounded-lg" />
            ))}
          </div>
        ) : filteredBookings.length > 0 ? (
          <div className="divide-y" data-testid="bookings-list">
            {filteredBookings.map((booking, index) => (
              <div key={booking.id} className="p-6" data-testid={`booking-${index}`}>
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-primary">{booking.name}</h3>
                      {getStatusBadge(booking.status || 'planificando')}
                      {booking.source_message_id && (
                        <Badge variant="outline" className="text-xs">
                          Desde mensaje
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-secondary font-medium mb-3">{booking.trip_title}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-2">
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${booking.email}`} className="hover:text-primary">
                          {booking.email}
                        </a>
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        <a href={`tel:${booking.phone}`} className="hover:text-primary">
                          {booking.phone}
                        </a>
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {booking.num_travelers} viajeros
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(booking.travel_date)}
                        {booking.end_date && ` - ${formatDate(booking.end_date)}`}
                      </span>
                    </div>

                    {booking.price && (
                      <p className="text-sm font-semibold text-green-600 mb-2">
                        Precio: {booking.price} €
                      </p>
                    )}

                    {booking.budget && (
                      <p className="text-sm text-muted-foreground mb-2">
                        <strong>Presupuesto:</strong> {booking.budget}
                      </p>
                    )}

                    {booking.special_requests && (
                      <p className="mt-3 text-sm bg-slate-50 p-3 rounded-lg">
                        <strong>Notas:</strong> {booking.special_requests}
                      </p>
                    )}

                    {booking.group_name && (
                      <p className="mt-2 text-sm text-muted-foreground">
                        <strong>Grupo:</strong> {booking.group_name}
                      </p>
                    )}
                  </div>

                  <div className="flex items-start gap-3">
                    <div>
                      <span className="text-xs text-muted-foreground flex items-center gap-1 mb-2 justify-end">
                        <Calendar className="w-3.5 h-3.5" />
                        Recibido: {formatDateTime(booking.created_at)}
                      </span>
                      <label className="text-xs text-muted-foreground block mb-1">Cambiar estado</label>
                      <Select
                        value={booking.status || 'planificando'}
                        onValueChange={(val) => updateStatus(booking.id, val)}
                      >
                        <SelectTrigger className="w-64">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {BOOKING_STATUS_OPTIONS.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            {statusFilter === 'all' ? 'No hay reservas todavía' : 'No hay reservas con este estado'}
          </div>
        )}
      </div>

      {/* Diálogo para crear nueva reserva */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Crear Nueva Reserva</DialogTitle>
            <DialogDescription>
              Introduce los datos de la nueva reserva
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="new_trip_id">Viaje (Opcional)</Label>
                <Select 
                  value={newBooking.trip_id} 
                  onValueChange={(value) => {
                    const selectedTrip = trips.find(t => t.id === value);
                    setNewBooking({ 
                      ...newBooking, 
                      trip_id: value,
                      trip_title: selectedTrip ? selectedTrip.title : newBooking.trip_title
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un viaje (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {trips.map(trip => (
                      <SelectItem key={trip.id} value={trip.id}>
                        {trip.title} - {trip.destination}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label htmlFor="new_trip_title">Título del Viaje *</Label>
                <Input 
                  id="new_trip_title"
                  value={newBooking.trip_title}
                  onChange={(e) => setNewBooking({ ...newBooking, trip_title: e.target.value })}
                  placeholder="Ej: Viaje a Japón personalizado"
                  required
                />
              </div>

              <div>
                <Label htmlFor="new_name">Nombre del cliente *</Label>
                <Input 
                  id="new_name"
                  value={newBooking.name}
                  onChange={(e) => setNewBooking({ ...newBooking, name: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="new_email">Email *</Label>
                <Input 
                  id="new_email"
                  type="email"
                  value={newBooking.email}
                  onChange={(e) => setNewBooking({ ...newBooking, email: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="new_phone">Teléfono *</Label>
                <Input 
                  id="new_phone"
                  type="tel"
                  value={newBooking.phone}
                  onChange={(e) => setNewBooking({ ...newBooking, phone: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="new_num_travelers">Número de viajeros *</Label>
                <Input 
                  id="new_num_travelers"
                  type="number"
                  min="1"
                  value={newBooking.num_travelers}
                  onChange={(e) => setNewBooking({ ...newBooking, num_travelers: parseInt(e.target.value) })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="new_travel_date">Fecha inicio *</Label>
                <Input 
                  id="new_travel_date"
                  type="date"
                  value={newBooking.travel_date}
                  onChange={(e) => setNewBooking({ ...newBooking, travel_date: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="new_end_date">Fecha fin</Label>
                <Input 
                  id="new_end_date"
                  type="date"
                  value={newBooking.end_date}
                  onChange={(e) => setNewBooking({ ...newBooking, end_date: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="new_price">Precio (€)</Label>
                <Input 
                  id="new_price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={newBooking.price}
                  onChange={(e) => setNewBooking({ ...newBooking, price: e.target.value })}
                  placeholder="Ej: 2500.00"
                />
              </div>

              <div>
                <Label htmlFor="new_budget">Presupuesto del cliente</Label>
                <Input 
                  id="new_budget"
                  value={newBooking.budget}
                  onChange={(e) => setNewBooking({ ...newBooking, budget: e.target.value })}
                  placeholder="Ej: 3000-3500 EUR"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="new_special_requests">Notas / Solicitudes especiales</Label>
                <Textarea 
                  id="new_special_requests"
                  value={newBooking.special_requests}
                  onChange={(e) => setNewBooking({ ...newBooking, special_requests: e.target.value })}
                  rows={4}
                  placeholder="Detalles y solicitudes especiales..."
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateBooking}
              className="bg-green-600 hover:bg-green-700"
            >
              Crear Reserva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
